import React from 'react';
import { motion } from 'framer-motion';
import { CircularProgressbar, buildStyles } from 'react-circular-progressbar';
import 'react-circular-progressbar/dist/styles.css';

const OverallPerformance = ({ avgScore, totalTests, improvement, theme }) => {
  const getScoreColor = () => {
    if (avgScore >= 70) return theme?.primary?.green?.[500];
    if (avgScore >= 50) return theme?.primary?.yellow?.[500];
    return theme?.primary?.red?.[500];
  };

  const getPerformanceMessage = () => {
    if (avgScore >= 80) return "Outstanding Performance! 🌟";
    if (avgScore >= 70) return "Great Progress! 🎯";
    if (avgScore >= 60) return "Good Effort! 💪";
    if (avgScore >= 50) return "Keep Practicing! 📚";
    return "Room for Improvement 🚀";
  };

  const getPerformanceDescription = () => {
    if (avgScore >= 80) return "You're excelling across all areas! Keep up the fantastic work.";
    if (avgScore >= 70) return "Strong performance with room to reach excellence.";
    if (avgScore >= 60) return "Good foundation built, focus on consistency.";
    if (avgScore >= 50) return "Making progress, additional practice will help.";
    return "Focus on fundamentals to build a stronger foundation.";
  };

  const styles = {
    container: {
      background: theme?.background?.secondary,
      borderRadius: '1.25rem',
      padding: '2rem',
      border: `1px solid ${theme?.borders?.primary}`,
      boxShadow: theme?.shadows?.base,
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
    },
    mainContent: {
      display: 'flex',
      alignItems: 'center',
      gap: '2rem',
      flex: 1,
    },
    leftSection: {
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
    },
    title: {
      fontSize: '1.5rem',
      fontWeight: '700',
      color: theme?.text?.primary,
      marginBottom: '0.5rem',
    },
    subtitle: {
      fontSize: '0.875rem',
      color: theme?.text?.secondary,
      marginBottom: '1.5rem',
    },
    performanceMessage: {
      color: theme?.text?.primary,
      fontSize: '1.25rem',
      fontWeight: '600',
      marginBottom: '0.75rem',
    },
    performanceDescription: {
      color: theme?.text?.secondary,
      fontSize: '0.875rem',
      lineHeight: '1.5',
      marginBottom: '1.5rem',
    },
    statsRow: {
      display: 'flex',
      gap: '1.5rem',
      alignItems: 'center',
    },
    statItem: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
    },
    statValue: {
      fontSize: '1.25rem',
      fontWeight: '700',
      color: theme?.text?.primary,
      marginBottom: '0.25rem',
    },
    statLabel: {
      fontSize: '0.75rem',
      color: theme?.text?.secondary,
      textAlign: 'center',
    },
    rightSection: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      minWidth: '160px',
    },
    progressWrapper: {
      width: '200px',
      height: '200px',
      marginBottom: '1rem',
    },
    improvementBadge: {
      background: improvement > 0 
        ? `linear-gradient(135deg, ${theme?.primary?.green?.[100]}20, ${theme?.primary?.green?.[200]}30)`
        : improvement < 0
        ? `linear-gradient(135deg, ${theme?.primary?.red?.[100]}20, ${theme?.primary?.red?.[200]}30)`
        : `linear-gradient(135deg, ${theme?.borders?.primary}20, ${theme?.borders?.primary}30)`,
      borderRadius: '1rem',
      padding: '0.5rem 1rem',
      border: `1px solid ${
        improvement > 0 
          ? theme?.primary?.green?.[300] + '40'
          : improvement < 0
          ? theme?.primary?.red?.[300] + '40'
          : theme?.borders?.primary + '40'
      }`,
      textAlign: 'center',
      minWidth: '120px',
    },
    improvementValue: {
      fontSize: '1rem',
      fontWeight: '600',
      color: improvement > 0 
        ? theme?.primary?.green?.[600] 
        : improvement < 0 
        ? theme?.primary?.red?.[600]
        : theme?.text?.secondary,
    },
    improvementLabel: {
      fontSize: '0.7rem',
      color: theme?.text?.secondary,
      marginTop: '0.25rem',
    },
  };

  return (
    <motion.div
      style={styles.container}
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.5 }}
    >
      <div style={styles.mainContent}>
        <div style={styles.leftSection}>
          <h2 style={styles.title}>Overall Performance</h2>
          <p style={styles.subtitle}>Your comprehensive test analytics</p>
          
          <div style={styles.performanceMessage}>
            {getPerformanceMessage()}
          </div>
          
          <p style={styles.performanceDescription}>
            {getPerformanceDescription()}
          </p>

          {/* <div style={styles.statsRow}>
            <div style={styles.statItem}>
              <div style={styles.statValue}>{totalTests}</div>
              <div style={styles.statLabel}>Tests<br/>Completed</div>
            </div>
            <div style={styles.statItem}>
              <div style={styles.statValue}>{avgScore}%</div>
              <div style={styles.statLabel}>Average<br/>Score</div>
            </div>
          </div> */}
        </div>

        <div style={styles.rightSection}>
          <div style={styles.progressWrapper}>
            <CircularProgressbar
              value={avgScore}
              text={`${avgScore}%`}
              circleRatio={0.75}
              styles={buildStyles({
                rotation: 1/2 + 1/8,
                strokeLinecap: 'round',
                textSize: '1.4rem',
                pathTransitionDuration: 1.5,
                pathColor: getScoreColor(),
                textColor: theme?.text?.primary,
                trailColor: theme?.borders?.primary,
                backgroundColor: 'transparent',
                strokeWidth: 8,
              })}
            />
          </div>

          {/* {improvement !== 0 && (
            <div style={styles.improvementBadge}>
              <div style={styles.improvementValue}>
                {improvement > 0 ? `+${improvement}%` : `${improvement}%`}
              </div>
              <div style={styles.improvementLabel}>
                vs first test
              </div>
            </div>
          )} */}
        </div>
      </div>
    </motion.div>
  );
};

export default OverallPerformance;